import type { ComponentType } from "react";
import { cn } from "@nexus/ui";
import { useTabIndicator } from "./useTabIndicator";

export interface DrawerTabDef<T extends string> {
  id: T;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  count?: number;
  hasUnsavedChanges?: boolean;
  /** Destructive styling — used for Danger Zone. */
  tone?: "danger";
  testId?: string;
}

interface DrawerTabStripProps<T extends string> {
  tabs: Array<DrawerTabDef<T>>;
  activeTab: T;
  onTabChange: (tab: T) => void;
}

/** Animated tab strip with a sliding underline indicator, shared by the Org, Person, and Role drawers. */
export function DrawerTabStrip<T extends string>({ tabs, activeTab, onTabChange }: DrawerTabStripProps<T>) {
  const { indicator, registerTabRef } = useTabIndicator(tabs.findIndex((t) => t.id === activeTab));
  const activeTone = tabs.find((tab) => tab.id === activeTab)?.tone;

  return (
    <div className="relative flex overflow-x-auto border-b border-border px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {tabs.map((t, i) => (
        <button
          key={t.id}
          type="button"
          ref={registerTabRef(i)}
          onClick={() => onTabChange(t.id)}
          data-testid={t.testId}
          className={cn(
            "relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 py-2.5 text-[12px] font-medium transition-colors",
            t.tone === "danger" && "ml-auto",
            t.tone === "danger"
              ? activeTab === t.id
                ? "text-destructive"
                : "text-destructive/80 hover:text-destructive"
              : activeTab === t.id
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
          )}
        >
          <t.Icon className="h-3 w-3" />
          {t.label}
          {t.hasUnsavedChanges && (
            <span
              className="absolute right-0.5 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-warning-subtle-foreground"
              data-testid={t.testId ? `${t.testId}-unsaved` : undefined}
              aria-hidden
            />
          )}
          {t.count !== undefined && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 font-mono text-[10px] text-muted-foreground">
              {t.count}
            </span>
          )}
        </button>
      ))}
      <div
        className={cn("absolute bottom-0 h-0.5", activeTone === "danger" ? "bg-destructive" : "bg-primary")}
        style={{
          left: indicator.left,
          width: indicator.width,
          transition: "left 200ms cubic-bezier(0.4,0,0.2,1), width 200ms cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </div>
  );
}
